'use client'

import Link from 'next/link'
import React, { useState } from 'react'
import { SendIcon } from 'lucide-react'

import { CMSLink } from '@/components/Link'
import { Logo } from '@/components/Logo/Logo'
import { Media } from '@/components/Media'
import { RevealSection } from '@/components/RevealSection'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getSocialLogo } from '@/utilities/global/getSocialLogo'
import { cn } from '@/utilities/ui'

import { FooterSignOut } from './FooterSignOut'

import type { Footer, Header, SocialLink } from '@/payload-types'

interface FooterClientProps {
  headerData: Header
  footerData: Footer
  socialLinks: SocialLink
}

export function FooterClient({ headerData, footerData, socialLinks }: FooterClientProps) {
  const leftNav = headerData?.leftNav
  const navItems = footerData?.navItems || []
  const updates = footerData?.updates || {}
  const tagline = footerData?.tagline || ''
  const { title, blurb } = updates

  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
    'idle',
  )
  const [subscribeMessage, setSubscribeMessage] = useState<string>('')

  const handleSubscribeSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const email = new FormData(form).get('email')
    if (typeof email !== 'string' || !email.trim()) {
      setSubscribeStatus('error')
      setSubscribeMessage('Please enter your email.')
      return
    }
    setSubscribeStatus('loading')
    setSubscribeMessage('')
    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: email.trim() }),
      })
      const data = (await res.json()) as { ok?: boolean; errors?: { message: string }[] }
      if (!res.ok) {
        const message = data.errors?.[0]?.message ?? 'Something went wrong. Please try again.'
        setSubscribeStatus('error')
        setSubscribeMessage(message)
        return
      }
      setSubscribeStatus('success')
      setSubscribeMessage('Thanks for subscribing!')
      form.reset()
    } catch {
      setSubscribeStatus('error')
      setSubscribeMessage('Something went wrong. Please try again.')
    }
  }

  return (
    <footer className="relative z-10 mt-auto min-h-24 w-full bg-background pt-10 pb-1 text-foreground md:pt-16 md:pb-10">
      <div className="wrapper flex flex-col gap-12">
        {/* Row 1 */}
        <div className="order-1 grid gap-12 xs:gap-8 md:grid-cols-2">
          {/* Navigation */}
          <RevealSection direction="none">
            <nav className="grid w-full justify-items-center gap-3 xs:grid-flow-col xs:grid-cols-2 xs:grid-rows-5 md:grid-cols-3 md:grid-rows-3 md:justify-items-start">
              {navItems.map(({ link }, i) => {
                const isQueryString = link.url?.includes('?')

                if (isQueryString || link.type !== 'reference') {
                  return (
                    <Link
                      key={i}
                      href={link.url as string}
                      target={link.newTab ? '_blank' : '_self'}
                      rel={link.newTab ? 'noopener noreferrer' : undefined}
                      className="block font-body text-reg text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0"
                    >
                      {link.label}
                    </Link>
                  )
                }

                return (
                  <CMSLink
                    key={i}
                    className="block font-body text-reg text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-0"
                    {...link}
                  />
                )
              })}
            </nav>
          </RevealSection>

          {/* Updates */}
          <RevealSection
            direction="none"
            delay={0.1}
            className="space-y-2 justify-self-center text-center md:max-w-[40ch] md:space-y-4 md:justify-self-end md:text-right"
          >
            <h2 className="font-display text-xl font-normal text-foreground md:text-2xl">
              {title}
            </h2>
            <p className="font-body text-base text-foreground md:text-lg">{blurb}</p>
            <form
              onSubmit={handleSubscribeSubmit}
              className="flex w-full flex-col items-center gap-2 md:items-end md:gap-2"
            >
              <div className="flex w-full items-center justify-center gap-2 md:justify-end">
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  aria-label="Enter your email"
                  disabled={subscribeStatus === 'loading'}
                  autoComplete="email"
                  required
                />
                <Button
                  type="submit"
                  variant="icon"
                  icon={false}
                  className="size-11 p-1"
                  disabled={subscribeStatus === 'loading'}
                >
                  <span className="sr-only">Subscribe</span>
                  <SendIcon className="size-4.5" />
                </Button>
              </div>
              {subscribeMessage && (
                <p
                  className={cn(
                    'w-full text-center font-body text-sm md:text-right',
                    subscribeStatus === 'success' && 'text-foreground',
                    subscribeStatus === 'error' && 'text-destructive',
                  )}
                >
                  {subscribeMessage}
                </p>
              )}
            </form>
          </RevealSection>
        </div>

        {/* Row 2 */}
        <div className="order-3 grid gap-4 md:order-2 md:grid-cols-2">
          <RevealSection
            direction="none"
            delay={0.2}
            className="flex flex-wrap gap-3 justify-self-center text-center md:justify-self-start md:text-left"
          >
            {socialLinks?.links?.map(({ link }, i) => {
              const logoImage = getSocialLogo(link.label.toLowerCase().replace(' ', ''))

              if (!logoImage) return null

              return (
                <Link
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={i}
                  className="flex size-12 items-center justify-center rounded-xl bg-neutral-100 p-1 font-body text-reg transition-colors duration-200 hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-2"
                >
                  <Media
                    resource={logoImage.src}
                    context="icon"
                    alt={link.label}
                    className="h-auto w-6 grayscale"
                    width={logoImage.width}
                    height={logoImage.height}
                  />
                </Link>
              )
            })}
          </RevealSection>
          <RevealSection
            direction="none"
            delay={0.3}
            className="space-y-2 justify-self-center text-center font-body text-sm text-foreground md:max-w-max md:justify-self-end md:text-right"
          >
            <p>
              <Link href="/privacy-policy">Privacy Policy</Link>{' '}
              <span className="text-muted-foreground">&nbsp;|&nbsp;</span>{' '}
              <Link href="/terms-of-service">Terms of Service</Link>
              <span className="text-muted-foreground">&nbsp;|&nbsp;</span>{' '}
              <button className="termly-display-preferences cursor-pointer">
                Update Cookie Preferences
              </button>
            </p>
            <p>&copy; 2026 MAHA Action. All rights reserved.</p>
          </RevealSection>
        </div>

        {/* Row 3 */}
        <div className="relative order-2 grid items-center justify-items-center gap-4 md:order-3 md:grid-cols-2 md:pt-8 md:before:absolute md:before:top-0 md:before:left-0 md:before:block md:before:h-px md:before:w-full md:before:rounded-full md:before:bg-border md:before:content-['']">
          <RevealSection
            direction="none"
            delay={0.4}
            className="justify-self-center md:justify-self-start"
          >
            <Link href="/" className={cn('block')}>
              {leftNav?.logo ? (
                <Media
                  resource={leftNav.logo}
                  className="h-auto w-[165px] object-cover object-center md:w-full"
                  pictureClassName="h-auto w-[165px] object-cover object-center md:w-full"
                  imgClassName={cn('h-auto w-[165px] object-cover object-center md:w-full')}
                />
              ) : (
                <Logo className="w-[165px] min-w-16 md:w-full" />
              )}
            </Link>
          </RevealSection>
          <RevealSection
            direction="none"
            delay={0.5}
            className="flex flex-col gap-6 text-center md:text-right"
          >
            <FooterSignOut />
            <div>
              <h3 className="mb-2 font-display text-xl font-normal text-foreground">
                &ldquo;Make America Healthy Again&reg;&rdquo;
              </h3>
              <p className="font-body text-reg text-foreground">{tagline}</p>
            </div>
          </RevealSection>
        </div>
      </div>
    </footer>
  )
}
